import React, { useState, createContext } from 'react'
import {
  Box,
  Divider,
  Stack,
  Typography,
  useTheme,
  Button,
} from '@mui/material'
import Collapse from '@mui/material/Collapse'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableSortLabel from '@mui/material/TableSortLabel'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Paper from '@mui/material/Paper'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import { visuallyHidden } from '@mui/utils'
import { gql, useQuery } from '@apollo/client'
import Loader from 'components/Loader'
import numeral from 'numeral'
import {
  Company,
  PreQualifiedSite,
  Score,
  ScoreSubmission,
} from 'utils/__generated__/graphql'
import AddScore from './AddScore'

const LINKED_FILE_FRAGMENT = gql`
  fragment LinkedFileFragment on LinkedFile {
    dataroom
    dataroomName
    stage
    stageName
    file
    filename
    url
    mimetype
  }
`

const SCORE_FRAGMENT = gql`
  ${LINKED_FILE_FRAGMENT}
  fragment ScoreFragment on Score {
    id
    value
    subscores {
      analyze {
        value
        description
        linkedFiles {
          ...LinkedFileFragment
        }
      }
      interconnect {
        value
        description
        linkedFiles {
          ...LinkedFileFragment
        }
      }
      landowner {
        value
        description
        linkedFiles {
          ...LinkedFileFragment
        }
      }
      permitting {
        value
        description
        linkedFiles {
          ...LinkedFileFragment
        }
      }
      offtake {
        value
        description
        linkedFiles {
          ...LinkedFileFragment
        }
      }
      taxCredits {
        value
        description
        linkedFiles {
          ...LinkedFileFragment
        }
      }
      buildability {
        value
        description
        linkedFiles {
          ...LinkedFileFragment
        }
      }
    }
  }
`

export type ScoreContextType = {
  scoreSubmissions: [ScoreSubmission]
  companies: [Company]
  sites: [PreQualifiedSite]
}

export const ScoreContext = createContext<ScoreContextType>(
  {} as ScoreContextType
)

const FIND_SCORE_SUBMISSIONS_ADMIN = gql`
  ${SCORE_FRAGMENT}
  query FIND_SCORE_SUBMISSIONS_ADMIN($filter: JSON!) {
    scoreSubmissions: findScoreSubmissionsAdmin(filter: $filter) {
      id
      user {
        firstName
        lastName
      }
      company {
        name
      }
      site {
        name
      }
      score {
        ...ScoreFragment
      }
      originalScore {
        ...ScoreFragment
      }
      createdAt
    }
    companies: findCompanies(filter: {}) {
      id
      name
      users {
        id
        firstName
        lastName
      }
    }
    sites: findSitesAdmin(filter: {}) {
      id
      name
      score {
        ...ScoreFragment
      }
    }
  }
`

const SCORE_LABEL_MAP: { [key: string]: string } = {
  analyze: 'Analyze',
  interconnect: 'Interconnect',
  landowner: 'Landowner Participation',
  permitting: 'Permitting',
  offtake: 'Offtake',
  taxCredits: 'Tax Credits',
  buildability: 'Buildability',
}

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1
  }
  if (b[orderBy] > a[orderBy]) {
    return 1
  }
  return 0
}

type Order = 'asc' | 'desc'

function getComparator<Key extends keyof any>(
  order: Order,
  orderBy: Key
): (
  a: { [key in Key]: number | string },
  b: { [key in Key]: number | string }
) => number {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy)
}

type TableRowType = {
  company: string
  listing: string
  date: number
  user: string
  score: Score
  originalScore: Score
}

function Row(props: { row: TableRowType }) {
  const { row } = props
  const [open, setOpen] = useState(false)
  const theme = useTheme()

  return (
    <React.Fragment>
      <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
        <TableCell>
          <Button
            aria-label="expand row"
            endIcon={open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            size="small"
            onClick={() => setOpen(!open)}
          >
            Changes
          </Button>
        </TableCell>
        <TableCell component="th" scope="row">
          {row.company}
        </TableCell>
        <TableCell>{row.listing}</TableCell>
        <TableCell>{new Date(row.date).toLocaleDateString('en-US')}</TableCell>
        <TableCell>{row.user}</TableCell>
        <TableCell>{numeral(row.score.value).format('0.[0]')}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell sx={{ border: 'unset', p: 0 }} colSpan={7}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ m: 1 }}>
              <Typography
                variant="h6"
                gutterBottom
                component="div"
                sx={{ color: theme.palette.primary.light }}
              >
                Changes
              </Typography>
              <Table
                size="small"
                aria-label="changes"
                sx={{ margin: '0 auto' }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        color: theme.palette.textColor.main,
                        fontWeight: 'bold',
                      }}
                    >
                      Subscore
                    </TableCell>
                    <TableCell
                      sx={{
                        color: theme.palette.textColor.main,
                        fontWeight: 'bold',
                      }}
                    >
                      New Value
                    </TableCell>
                    <TableCell
                      sx={{
                        color: theme.palette.textColor.main,
                        fontWeight: 'bold',
                      }}
                    >
                      Original Value
                    </TableCell>
                    <TableCell
                      sx={{
                        color: theme.palette.textColor.main,
                        fontWeight: 'bold',
                      }}
                    >
                      Description
                    </TableCell>
                    <TableCell
                      sx={{
                        color: theme.palette.textColor.main,
                        fontWeight: 'bold',
                      }}
                    >
                      Files
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.keys(row?.originalScore?.subscores || {})
                    .filter(k => k !== '__typename')
                    .map((key, i) => (
                      <TableRow
                        key={String(i)}
                        sx={{
                          backgroundColor:
                            !Object.is(
                              //@ts-expect-error this needs a fix
                              row?.originalScore?.subscores?.[key]?.value,
                              //@ts-expect-error this needs a fix
                              row?.score?.subscores?.[key]?.value
                            ) && key !== 'analyze'
                              ? theme.palette.primary.main
                              : '',
                        }}
                      >
                        <TableCell
                          sx={{
                            color: theme.palette.textColor.main,
                          }}
                          component="th"
                          scope="row"
                        >
                          {SCORE_LABEL_MAP[`${key || ''}`]}
                        </TableCell>
                        <TableCell
                          sx={{
                            color: theme.palette.textColor.main,
                          }}
                        >
                          {/* @ts-expect-error this needs a fix */}
                          {row?.score?.subscores?.[key]?.value}
                        </TableCell>
                        <TableCell
                          sx={{
                            color: theme.palette.textColor.main,
                          }}
                        >
                          {key === 'analyze'
                            ? Math.round(
                                //@ts-expect-error this needs a fix
                                row?.originalScore?.subscores?.[key]?.value
                              )
                            : //@ts-expect-error this needs a fix
                              row?.originalScore?.subscores?.[key]?.value}
                        </TableCell>
                        <TableCell
                          sx={{
                            color: theme.palette.textColor.main,
                            maxWidth: 640,
                          }}
                        >
                          {/* @ts-expect-error this needs a fix */}
                          {row?.score?.subscores?.[key]?.description}
                        </TableCell>
                        <TableCell
                          sx={{
                            color: theme.palette.textColor.main,
                            maxWidth: 300,
                          }}
                        >
                          {(() => {
                            const subscore = (
                              row?.score?.subscores as Record<string, any>
                            )?.[key]
                            const files = subscore?.linkedFiles || []
                            if (files.length === 0) return null
                            return (
                              <Stack spacing={0.25}>
                                {files.map((lf: any) => (
                                  <Typography
                                    key={lf.file}
                                    variant="caption"
                                    component="a"
                                    href={lf.url}
                                    download
                                    rel="noopener noreferrer"
                                    sx={{
                                      color: theme.palette.primary.light,
                                      textDecoration: 'none',
                                      '&:hover': {
                                        textDecoration: 'underline',
                                      },
                                    }}
                                  >
                                    {lf.filename}
                                  </Typography>
                                ))}
                              </Stack>
                            )
                          })()}
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  )
}

interface HeadCell {
  id: string
  label: string
  numeric: boolean
}

interface EnhancedTableProps {
  onRequestSort: (event: React.MouseEvent<unknown>, property: string) => void
  order: Order
  orderBy: string
  rowCount: number
}

const headCells: readonly HeadCell[] = [
  {
    id: 'company',
    label: 'Company',
    numeric: false,
  },
  {
    id: 'listing',
    label: 'Listing',
    numeric: false,
  },
  {
    id: 'date',
    label: 'Date',
    numeric: true,
  },
  {
    id: 'user',
    label: 'User',
    numeric: false,
  },
  {
    id: 'scoreValue',
    label: 'Score',
    numeric: true,
  },
]

function EnhancedTableHead(props: EnhancedTableProps) {
  const { order, orderBy, onRequestSort } = props
  const createSortHandler =
    (property: string) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, property)
    }

  const theme = useTheme()

  return (
    <TableHead>
      <TableRow>
        <TableCell />
        {headCells.map(headCell => (
          <TableCell
            sx={{
              color: theme.palette.textColor.main,
              fontWeight: 'bold',
            }}
            key={headCell.id}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  )
}

function Readiness() {
  const [order, setOrder] = useState<Order>('asc')
  const [orderBy, setOrderBy] = useState('company')

  const theme = useTheme()

  const { loading, error, data } = useQuery(FIND_SCORE_SUBMISSIONS_ADMIN, {
    variables: { filter: { 'score.isOriginal': false } },
  })

  const handleRequestSort = (
    _: React.MouseEvent<unknown>,
    property: string
  ) => {
    const isAsc = orderBy === property && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(property)
  }

  if (loading) return <Loader />
  if (error) return <p>error...</p>

  const { scoreSubmissions } = data

  const rows = scoreSubmissions.map((submission: ScoreSubmission) => ({
    company: submission?.company?.name,
    listing: submission?.site?.name,
    date: new Date(submission.createdAt).getTime(),
    user: `${submission?.user?.firstName} ${submission?.user?.lastName}`,
    score: submission?.score,
    scoreValue: submission?.score?.value,
    originalScore: submission?.originalScore,
    originalScoreValue: submission?.originalScore?.value,
  }))

  const visibleRows = [...rows].sort(getComparator(order, orderBy))

  return (
    <ScoreContext.Provider value={data}>
      <Stack spacing={1}>
        <Box>
          <Stack
            direction="row"
            sx={{ justifyContent: 'space-between', mb: 1 }}
          >
            <Stack direction="row" spacing={1} sx={{ alignItems: 'baseline' }}>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 'bold',
                  color: theme.palette.textColor.main,
                }}
              >
                Readiness
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: theme.palette.textColor.main,
                }}
              >
                <i>Scores entered by users</i>
              </Typography>
            </Stack>
            <AddScore />
          </Stack>
          <Divider sx={{ bgcolor: theme.palette.textColor.main }} />
        </Box>
        <TableContainer component={Paper} variant="outlined" elevation={0}>
          <Table aria-label="collapsible table">
            <EnhancedTableHead
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              rowCount={rows.length}
            />
            <TableBody>
              {visibleRows.map((row: TableRowType, i: number) => (
                <Row key={String(i)} row={row} />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Stack>
    </ScoreContext.Provider>
  )
}

export default Readiness
